import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Calendar, 
  Clock, 
  Play, 
  Pause, 
  Settings, 
  Mail, 
  Users, 
  CheckCircle, 
  AlertCircle,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Loader
} from 'lucide-react';

interface Report {
  id: string;
  name: string;
  description: string;
  report_type: string;
  status: string;
  last_generated_at: string | null;
  schedule: string | null | { frequency?: string; [key: string]: any };
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface ScheduledReportsProps {
  reports: Report[];
  isLoading?: boolean;
}

export function ScheduledReports({ reports, isLoading }: ScheduledReportsProps) {
  const [activeReports, setActiveReports] = useState<string[]>(
    reports.filter(r => r.schedule && r.status !== 'paused').map(r => r.id)
  );

  const toggleReportStatus = (reportId: string) => {
    setActiveReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const getScheduleInfo = (schedule: string | any) => {
    // Handle cases where schedule might be an object instead of a string
    let scheduleString = schedule;
    if (typeof schedule === 'object' && schedule !== null) {
      scheduleString = schedule.frequency || schedule.type || 'custom';
    }
    
    const scheduleMap: Record<string, { label: string; next: string; frequency: string }> = {
      'daily': { 
        label: 'Daily', 
        next: 'Tomorrow at 9:00 AM', 
        frequency: 'Every day at 9:00 AM' 
      },
      'weekly': { 
        label: 'Weekly', 
        next: 'Next Monday at 9:00 AM', 
        frequency: 'Every Monday at 9:00 AM' 
      },
      'monthly': { 
        label: 'Monthly', 
        next: 'Next month on the 1st at 9:00 AM', 
        frequency: '1st of every month at 9:00 AM' 
      },
      'quarterly': { 
        label: 'Quarterly', 
        next: 'Next quarter on the 1st at 9:00 AM', 
        frequency: 'Every quarter on the 1st at 9:00 AM' 
      }
    };
    return scheduleMap[scheduleString] || { label: String(scheduleString || 'Custom'), next: 'Not scheduled', frequency: 'Custom schedule' };
  };

  const getStatusColor = (status: string, isActive: boolean) => {
    if (!isActive) return 'secondary' as const;
    switch (status) {
      case 'completed': return 'default' as const;
      case 'generating': return 'secondary' as const;
      case 'failed': return 'destructive' as const;
      default: return 'secondary' as const;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
              <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Scheduled Reports</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">Manage your automated report generation</p>
            </div>
          </div>
        </div>
        <Card className="p-12 text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading scheduled reports...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
            <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Scheduled Reports</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">Manage your automated report generation</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
          <Calendar className="w-4 h-4 mr-2" />
          Schedule New Report
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <Calendar className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Scheduled</p>
              <p className="text-lg font-semibold text-gray-900">{reports.length}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <Play className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-lg font-semibold text-gray-900">{activeReports.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
              <Pause className="w-4 h-4 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Paused</p>
              <p className="text-lg font-semibold text-gray-900">{reports.length - activeReports.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <CheckCircle className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">This Month</p>
              <p className="text-lg font-semibold text-gray-900">
                {reports.filter(r => {
                  if (!r.last_generated_at) return false;
                  const lastGenerated = new Date(r.last_generated_at);
                  const thisMonth = new Date();
                  return lastGenerated.getMonth() === thisMonth.getMonth() && 
                         lastGenerated.getFullYear() === thisMonth.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Scheduled Reports List */}
      {reports.length > 0 ? (
        <div className="space-y-4">
          {reports.map((report) => {
            const isActive = activeReports.includes(report.id);
            const scheduleInfo = getScheduleInfo(report.schedule || '');
            
            return (
              <Card key={report.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Report Icon */}
                    <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-green-600" />
                    </div>

                    {/* Report Details */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{report.name}</h3>
                        <Badge variant={getStatusColor(report.status, isActive)}>
                          {isActive ? (
                            <>
                              <Play className="w-3 h-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <Pause className="w-3 h-3 mr-1" />
                              Paused
                            </>
                          )}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {scheduleInfo.label}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">{report.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Clock className="w-4 h-4 mr-2" />
                          <div>
                            <p className="font-medium">Next Run</p>
                            <p className="text-xs">{isActive ? scheduleInfo.next : 'Paused'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Calendar className="w-4 h-4 mr-2" />
                          <div>
                            <p className="font-medium">Last Generated</p>
                            <p className="text-xs">{formatDate(report.last_generated_at)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-gray-600 dark:text-gray-400">
                          <Users className="w-4 h-4 mr-2" />
                          <div>
                            <p className="font-medium">Created by</p>
                            <p className="text-xs">{report.created_by}</p>
                          </div>
                        </div>
                      </div>

                      {/* Schedule Details */}
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                            <span className="text-sm text-gray-700">{String(scheduleInfo.frequency || 'Custom schedule')}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500">
                            <Mail className="w-3 h-3 mr-1" />
                            Auto-email enabled
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleReportStatus(report.id)}
                    >
                      {isActive ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </>
                      )}
                    </Button>
                    
                    <div className="relative group">
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                      
                      {/* Dropdown Menu */}
                      <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Schedule
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </button>
                        <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          Settings
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Scheduled Reports</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
            You haven't scheduled any reports yet. Set up automated report generation to save time.
          </p>
          <Button className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Your First Report
          </Button>
        </Card>
      )}
    </div>
  );
} 